// src/pages/Campaigns.tsx
/**
 * Owner-only e-mail campaigns: write the episode announcement, preview it, test it, then send it to
 * registered users who are not members.
 *
 * Unlike the other admin screens this one is full-width — the compose box and the live preview need
 * to sit side by side, and squeezing both into the 1100px the invoicing page uses makes the preview
 * too narrow to judge.
 *
 * The safety rails here mirror the server's: sending stays locked until a test has gone out, the
 * recipient count must be typed rather than ticked, and the first wave is deliberately small. None
 * of that is decorative — every batch puts the wave cap and the typed count on the wire, and the
 * server re-checks both before it sends anything, because an e-mail cannot be recalled. A rail that
 * lives only in this file is a rail a second browser tab walks straight through.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import campaignApi from '../api/campaignApi';

const ADMIN = '/admin/campaigns';
const SEND_BATCH_SIZE = 50;
const SEND_MAX_BATCHES = 60;
/** First wave is small on purpose: the domain has no bulk-sending history to trade on yet. */
const WAVES = [100, 300, 10000];

/** Mirrors CampaignAudience on the server. ALL is for notices, never for an offer. */
type AudienceKind = 'NON_PREMIUM' | 'ALL';
/** Mirrors CampaignTemplate: the podcast shell, or the plain notice shell. */
type TemplateKind = 'PODCAST' | 'NOTICE';

type Campaign = {
    id: number;
    name: string;
    subject: string;
    body: string;
    headerLeft: string | null;
    headerRight: string | null;
    audience: AudienceKind;
    template: TemplateKind;
    status: string;
    totalRecipients: number | null;
    killSwitch: boolean;
    testSentAt: string | null;
    contentChangedSinceTest: boolean;
    sent: number;
    failed: number;
    skipped: number;
    pending: number;
    uncertain: number;
};

type Audience = {
    recipients: number;
    excludedMembers: number;
    sharedAddresses: number;
    optedOut: number;
};

type Recipient = { email: string; status: string; skipReason: string | null; error: string | null };

/** A notice carries its own tone, so a halted send can never be rendered in the green banner. */
type Notice = { text: string; tone: 'ok' | 'warn' };

const STATUS_LABEL: Record<string, string> = {
    DRAFT: 'Koncept',
    READY: 'Pripravená',
    SENDING: 'Posiela sa',
    PAUSED: 'Zastavená',
    SENT: 'Odoslaná',
};

/**
 * The human half of an error body, never the machine half.
 *
 * Three shapes reach this screen and they disagree about what `error` means. The controller's own
 * handler sends `{error: "<Slovak sentence>"}` — there `error` IS the message. The auth entry point
 * sends `{status, error: "token_expired", message: "<Slovak sentence>"}` and Boot's /error sends
 * `{timestamp, status, error: "Forbidden", message, path}`; in both of those `error` is a code or an
 * English reason phrase. Reading `error` first is how `token_expired` got rendered verbatim in an
 * all-Slovak UI — and, because /error always fills `error` in, it also made every Slovak fallback on
 * this page unreachable.
 *
 * Note that the entry point's status is 403 today, not 401 — see api/axiosInstance.ts, which is
 * where that distinction is drawn. It makes no difference here: this function reads the body and
 * never the status, which is exactly why it survived the change.
 */
function errText(err: any, fallback: string): string {
    const data = err?.response?.data;
    if (data && typeof data === 'object') {
        const message = typeof data.message === 'string' ? data.message.trim() : '';
        if (message) return message;
        // Sole field: that shape only comes from the controller's own handler, where it is prose.
        const sole = typeof data.error === 'string' ? data.error.trim() : '';
        if (sole && Object.keys(data).length === 1) return sole;
    }
    return fallback;
}

/**
 * "Epizóda 214" -> "Epizóda 214 (kópia)" -> "Epizóda 214 (kópia 2)". Campaigns are told apart by
 * name in the list, and two identically named ones is a genuine hazard once one of them has already
 * gone out to the whole audience.
 */
function copyName(current: string): string {
    const m = current.match(/^(.*?)\s*\(kópia(?:\s+(\d+))?\)$/);
    if (!m) return `${current} (kópia)`;
    return `${m[1]} (kópia ${m[2] ? parseInt(m[2], 10) + 1 : 2})`;
}

const Campaigns: React.FC = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useSelector((state: any) => state.auth);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<Notice | null>(null);
    const [busy, setBusy] = useState<'' | 'save' | 'arm' | 'test' | 'send' | 'stop' | 'resolve'>('');
    /**
     * Whether a send loop is still iterating, as opposed to whether a button is disabled.
     *
     * `busy` cannot answer that: Stop runs *during* a send and overwrites it, so the send loop's own
     * claim is lost and whatever Stop's `finally` writes becomes the truth. Blanking it there put
     * „Pokračovať“ back on screen under a loop that was still draining, and resuming from that button
     * cleared the kill switch the loop was about to read — so the operator's Stop was undone by their
     * own next click and the wave carried on. A ref rather than state because Stop's `finally` has to
     * read the current value, not the one captured when its handler was created.
     */
    const sending = useRef(false);

    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [name, setName] = useState(DEFAULT_NAME);
    const [subject, setSubject] = useState(DEFAULT_SUBJECT);
    const [body, setBody] = useState(DEFAULT_BODY);
    const [headerLeft, setHeaderLeft] = useState('');
    const [headerRight, setHeaderRight] = useState('');
    const [audienceKind, setAudienceKind] = useState<AudienceKind>('NON_PREMIUM');
    const [template, setTemplate] = useState<TemplateKind>('PODCAST');
    const [previewHtml, setPreviewHtml] = useState('');
    const [warnings, setWarnings] = useState<string[]>([]);
    const [audience, setAudience] = useState<Audience | null>(null);
    const [recipients, setRecipients] = useState<Recipient[]>([]);
    const [confirmCount, setConfirmCount] = useState('');
    const [testTo, setTestTo] = useState('');

    /** Green banner: the thing that was asked for happened. */
    const say = (text: string) => setNotice({ text, tone: 'ok' });
    /**
     * Amber banner: it happened, but not cleanly — so it must not look like the green one.
     *
     * A stopped send and a completed send reading alike is how the rest of the list ends up never
     * being sent, or sent a second time by someone who assumed nothing had gone out. A half-delivered
     * test reported as a clean success is the same failure one step earlier: the addresses that did
     * receive it are exactly the ones a second press mails twice.
     */
    const sayWarn = (text: string) => setNotice({ text, tone: 'warn' });

    // Third layer of defence. The route and the API each enforce this independently.
    useEffect(() => {
        if (isAuthenticated && !user?.isAdmin) navigate('/');
    }, [isAuthenticated, user, navigate]);

    /** Rendered by the server so the preview cannot drift from what actually sends. */
    useEffect(() => {
        const timer = setTimeout(async () => {
            try {
                const res = await campaignApi.post(`${ADMIN}/preview`, {
                    name, subject, body, headerLeft, headerRight, template,
                });
                setPreviewHtml(res.data?.html || '');
                setWarnings(res.data?.warnings || []);
            } catch {
                /* preview is advisory; a failure here must not block the page */
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [name, subject, body, headerLeft, headerRight, template]);

    const loadAudience = useCallback(async (kind: AudienceKind) => {
        try {
            const res = await campaignApi.get(`${ADMIN}/audience`, { params: { audience: kind } });
            setAudience(res.data);
        } catch (err: any) {
            setError(errText(err, 'Nepodarilo sa načítať počet príjemcov.'));
        }
    }, []);

    // The count on screen must be the count for the audience currently selected, or the operator
    // types a confirmation number that belongs to a different list.
    useEffect(() => {
        loadAudience(audienceKind);
    }, [audienceKind, loadAudience]);

    const loadCampaign = useCallback(async (id: number) => {
        const res = await campaignApi.get(`${ADMIN}/${id}`);
        setCampaign(res.data);
        return res.data as Campaign;
    }, []);

    const loadRecipients = useCallback(async (id: number) => {
        try {
            const res = await campaignApi.get(`${ADMIN}/${id}/recipients`, { params: { limit: 50 } });
            setRecipients(res.data || []);
        } catch {
            /* optional detail */
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        campaignApi
            .get(ADMIN)
            .then(async (res) => {
                const list = res.data || [];
                if (list.length > 0) {
                    const latest = await loadCampaign(list[0].id);
                    setName(latest.name);
                    setSubject(latest.subject);
                    setBody(latest.body);
                    setHeaderLeft(latest.headerLeft || '');
                    setHeaderRight(latest.headerRight || '');
                    setAudienceKind(latest.audience || 'NON_PREMIUM');
                    setTemplate(latest.template || 'PODCAST');
                    await loadRecipients(latest.id);
                }
            })
            .catch((err: any) => {
                setError(
                    errText(
                        err,
                        err?.response?.status === 403
                            ? 'Prístup zamietnutý. Účet nemá oprávnenie správcu (ROLE_ADMIN).'
                            : 'Nepodarilo sa načítať kampane.'
                    )
                );
            })
            .finally(() => setLoading(false));
    }, [loadCampaign, loadRecipients]);

    const onSave = async () => {
        setBusy('save');
        setError(null);
        try {
            const payload = { name, subject, body, headerLeft, headerRight, audience: audienceKind, template };
            const res = campaign
                ? await campaignApi.put(`${ADMIN}/${campaign.id}`, payload)
                : await campaignApi.post(ADMIN, payload);
            setCampaign(res.data);
            say('Uložené.');
        } catch (err: any) {
            setError(errText(err, 'Uloženie zlyhalo.'));
        } finally {
            setBusy('');
        }
    };

    /**
     * Start a fresh campaign.
     *
     * Without this the page was a one-way door: the mount effect loads the newest campaign and
     * nothing ever set `campaign` back to null, so the create branch of onSave became unreachable the
     * moment one campaign existed. And a campaign that has started sending refuses PUT with "vytvorte
     * kópiu" — advice the screen offered no way to follow.
     */
    const onNewCampaign = () => {
        setCampaign(null);
        setName(DEFAULT_NAME);
        setSubject(DEFAULT_SUBJECT);
        setBody(DEFAULT_BODY);
        setHeaderLeft('');
        setHeaderRight('');
        // These two decide WHO gets the mail and how it looks, and they are the only controls that
        // would otherwise survive a reset. Carrying ALL over from a legal notice into a fresh
        // campaign — whose body has just been reset to the membership pitch — would mail every
        // paying member the offer, which is the one outcome this module exists to prevent.
        setAudienceKind('NON_PREMIUM');
        setTemplate('PODCAST');
        // Both belong to the campaign that was on screen: the list would otherwise be read as this
        // one's, and the typed count would confirm a total that is no longer the one being sent.
        setRecipients([]);
        setConfirmCount('');
        setError(null);
        say('Nová kampaň — uložením vznikne nová, tá predchádzajúca zostáva nedotknutá.');
    };

    /** The same, but keeping what is on screen: last week's e-mail is the template for this week's. */
    const onDuplicate = () => {
        setCampaign(null);
        setName(copyName(name));
        setRecipients([]);
        setConfirmCount('');
        setError(null);
        say('Kópia pripravená — uložením vznikne nová kampaň, originál zostáva nedotknutý.');
    };

    const onArm = async () => {
        if (!campaign) return;
        setBusy('arm');
        setError(null);
        say('Pripravujem zoznam príjemcov…');
        try {
            // Save first, always. The server materialises against the campaign's STORED audience,
            // so arming with unsaved changes on screen builds the list for the previous selection
            // while the form shows the new one — and the select then locks, hiding the mismatch.
            const saved = await campaignApi.put(`${ADMIN}/${campaign.id}`, {
                name, subject, body, headerLeft, headerRight, audience: audienceKind, template,
            });
            setCampaign(saved.data);
            const res = await campaignApi.post(`${ADMIN}/${campaign.id}/arm`);
            setCampaign(res.data);
            await loadRecipients(campaign.id);
            say(`Pripravených ${res.data.totalRecipients} príjemcov.`);
        } catch (err: any) {
            setNotice(null);
            setError(errText(err, 'Prípravu zoznamu sa nepodarilo dokončiť.'));
        } finally {
            setBusy('');
        }
    };

    const onTest = async () => {
        if (!campaign) return;
        setBusy('test');
        setError(null);
        say('Posielam test…');
        try {
            const emails = testTo
                .split(',')
                .map((e) => e.trim())
                .filter(Boolean);
            const res = await campaignApi.post(`${ADMIN}/${campaign.id}/test`, { emails });
            await loadCampaign(campaign.id);
            const sentTo: string[] = res.data?.sentTo || [];
            // The server answers with both halves because one typo in five is the ordinary way a test
            // fails, and it attempts each address separately so the rest still go out. Reporting only
            // the successes turns that into a clean green "sent" — and the natural response to a test
            // that looks wrong is to press the button again, which re-mails everyone in `sentTo`.
            const rejected = Object.keys(res.data?.failed || {});
            if (rejected.length > 0) {
                sayWarn(
                    `Test odoslaný na: ${sentTo.join(', ')} — neprešlo: ${rejected.join(', ')}. ` +
                        'Opravte chybné adresy; ostatným už test odišiel a opakovaným odoslaním im ' +
                        'príde druhýkrát.'
                );
            } else {
                say(`Test odoslaný na: ${sentTo.join(', ')}`);
            }
        } catch (err: any) {
            setNotice(null);
            setError(errText(err, 'Test sa nepodarilo odoslať.'));
        } finally {
            setBusy('');
        }
    };

    /**
     * Drains the queue in batches. The server reports what is left; it owns the bookkeeping.
     *
     * Every request carries the wave cap and the count the owner typed, not just this batch's limit.
     * The loop is a convenience of this screen — the server has to be able to refuse a batch that
     * would take the campaign past the wave a human actually approved, whoever asked for it.
     */
    const onSend = async (cap: number) => {
        if (!campaign) return;
        sending.current = true;
        setBusy('send');
        setError(null);
        // The wave cap is cumulative ("send the first 300"), so the budget for this run is whatever
        // is left of it — not the whole number again.
        const alreadySent = campaign.sent ?? 0;
        const budget = Math.max(0, cap - alreadySent);
        // The number the human typed, as they typed it. canSend has already checked it against the
        // campaign's total; the server checks it again, which is the point of sending it.
        const typedCount = confirmCount.trim();
        let sentThisRun = 0;
        let stopped = false;
        try {
            for (let n = 0; n < SEND_MAX_BATCHES; n++) {
                if (sentThisRun >= budget) break;
                // Explicit `any`: the request lives in its own try so a 409 can be inspected, and an
                // annotated let keeps the definite-assignment check off a variable the catch never
                // falls past.
                let res: any;
                try {
                    res = await campaignApi.post(`${ADMIN}/${campaign.id}/send`, null, {
                        params: {
                            limit: Math.min(SEND_BATCH_SIZE, budget - sentThisRun),
                            cap,
                            confirm: typedCount,
                        },
                    });
                } catch (err: any) {
                    // A kill switch pulled between two batches is answered with 409 — but so is every
                    // other guard, and their messages are free text. Ask the campaign itself rather
                    // than matching on a sentence.
                    if (err?.response?.status === 409 && (await loadCampaign(campaign.id)).killSwitch) {
                        stopped = true;
                        break;
                    }
                    throw err;
                }
                const r = res.data || {};
                sentThisRun += r.sent || 0;
                // Keep client status in step with the server so the Stop button appears while the
                // send is actually running, and so a PAUSED response is acted on.
                if (r.status) {
                    setCampaign((prev) => (prev ? { ...prev, status: r.status } : prev));
                }
                say(`Posielam… ${alreadySent + sentThisRun} odoslaných, ${r.remaining ?? '?'} zostáva`);
                // Halted, not finished: the switch tripped part-way through this batch.
                if (r.stopped === true || r.status === 'PAUSED') {
                    stopped = true;
                    break;
                }
                if (r.done === true || r.capReached === true) break;
                if ((r.sent || 0) + (r.skipped || 0) + (r.failed || 0) === 0) break;
            }
            // Report the server's cumulative numbers, so the banner and the progress card agree.
            const fresh = await loadCampaign(campaign.id);
            await loadRecipients(campaign.id);
            if (stopped || fresh.killSwitch) {
                sayWarn(
                    `Zastavené — ${fresh.sent} odoslaných, ${fresh.pending} zostáva v rade. ` +
                        'Zvyšok pošlete tlačidlom „Pokračovať“.'
                );
            } else {
                say(
                    `Hotovo — ${fresh.sent} odoslaných, ${fresh.skipped} preskočených, ${fresh.failed} zlyhalo.` +
                        (fresh.pending > 0 ? ` ${fresh.pending} čaká na ďalšiu vlnu.` : '')
                );
            }
        } catch (err: any) {
            setNotice(null);
            setError(errText(err, 'Odosielanie zlyhalo.'));
        } finally {
            sending.current = false;
            setBusy('');
        }
    };

    const onStop = async () => {
        if (!campaign) return;
        setBusy('stop');
        try {
            const res = await campaignApi.post(`${ADMIN}/${campaign.id}/stop`);
            setCampaign(res.data);
            sayWarn('Zastavené. Zvyšok sa dá poslať neskôr tlačidlom „Pokračovať“.');
        } catch (err: any) {
            setError(errText(err, 'Zastavenie zlyhalo.'));
        } finally {
            // Hand the flag back if a send loop is still draining; it clears it in its own finally.
            setBusy(sending.current ? 'send' : '');
        }
    };

    const onResume = async () => {
        if (!campaign) return;
        setBusy('stop');
        try {
            const res = await campaignApi.post(`${ADMIN}/${campaign.id}/resume`);
            setCampaign(res.data);
            say('Pokračujeme — zvyšok sa dá poslať.');
        } catch (err: any) {
            setError(errText(err, 'Obnovenie zlyhalo.'));
        } finally {
            setBusy(sending.current ? 'send' : '');
        }
    };

    const onRequeueFailed = async () => {
        if (!campaign) return;
        setBusy('stop');
        try {
            const res = await campaignApi.post(`${ADMIN}/${campaign.id}/requeue-failed`);
            setCampaign(res.data);
            await loadRecipients(campaign.id);
            say('Neúspešní príjemcovia sú znova v rade.');
        } catch (err: any) {
            setError(errText(err, 'Nepodarilo sa vrátiť neúspešných do radu.'));
        } finally {
            setBusy('');
        }
    };

    /**
     * Decide the rows the sender refused to decide for itself.
     *
     * A recipient stays in SENDING when the mail went out but the ledger write that records it did
     * not — the one case where the server cannot know what happened, so it deliberately guesses
     * neither way. Those rows are also why the campaign never reaches SENT. Only a human with the
     * relay log can resolve them, and until this existed there was no way to say so.
     */
    const onResolveUncertain = async (as: 'SENT' | 'FAILED') => {
        if (!campaign) return;
        // FAILED is the re-sendable one, so it is the one that can put a second copy of the same
        // e-mail in someone's inbox. Say that in words before it happens, not in a tooltip.
        if (
            as === 'FAILED' &&
            !window.confirm(
                `Označiť ${campaign.uncertain} príjemcov ako zlyhaných?\n\n` +
                    'Týmto ľuďom e-mail s najväčšou pravdepodobnosťou UŽ odišiel — nepodaril sa len ' +
                    'zápis do denníka. Ako zlyhaní sa dajú vrátiť do radu tlačidlom „Skúsiť ' +
                    'neúspešných znova“, a vtedy im rovnaký e-mail príde druhýkrát.'
            )
        ) {
            return;
        }
        setBusy('resolve');
        setError(null);
        try {
            const res = await campaignApi.post(`${ADMIN}/${campaign.id}/resolve-uncertain`, null, {
                params: { as },
            });
            const resolved = res.data?.resolved ?? 0;
            await loadCampaign(campaign.id);
            await loadRecipients(campaign.id);
            say(
                as === 'SENT'
                    ? `Vyriešené — ${resolved} označených ako odoslané. Kampaň sa uzavrie až ďalším ` +
                      'odoslaním, ktoré nájde prázdny rad — rozhodnutie o uzavretí je zámerne na ' +
                      'jednom mieste, v odosielacej slučke.'
                    : `Vyriešené — ${resolved} označených ako zlyhané. Znova sa pošlú až po kliknutí na „Skúsiť neúspešných znova“.`
            );
        } catch (err: any) {
            setError(errText(err, 'Neisté záznamy sa nepodarilo vyriešiť.'));
        } finally {
            setBusy('');
        }
    };

    const armed = campaign?.status === 'READY' || campaign?.status === 'SENDING' || campaign?.status === 'PAUSED';
    const tested = !!campaign?.testSentAt && !campaign?.contentChangedSinceTest;
    const expected = campaign?.totalRecipients ?? audience?.recipients ?? 0;
    const confirmed = confirmCount.trim() === String(expected) && expected > 0;
    // The kill switch is deliberately part of this. `armed` includes PAUSED, so without it the send
    // button sat enabled right beside „Pokračovať“ — two buttons offering to do the same thing, one
    // of which the server now refuses with a 409. Resume is the only correct next step.
    const canSend = !!campaign && armed && tested && confirmed && !campaign.killSwitch && !busy;
    const nextWave = WAVES.find((w) => w > (campaign?.sent ?? 0)) ?? WAVES[WAVES.length - 1];

    return (
        <Page>
            <Top>
                <Back onClick={() => navigate(-1)}>&larr;</Back>
                <Heading>E-mailové kampane</Heading>
                {campaign && <Chip $tone={campaign.status}>{STATUS_LABEL[campaign.status] || campaign.status}</Chip>}
            </Top>

            {error && <Banner role="alert">{error}</Banner>}
            {notice && !error && (
                <Banner as="div" $ok={notice.tone === 'ok'} $warn={notice.tone === 'warn'}>
                    {notice.text}
                </Banner>
            )}

            <Cols>
                <Card>
                    <CardTitle>Obsah</CardTitle>
                    <Field>
                        <Label htmlFor="c-name">Názov kampane</Label>
                        <Input id="c-name" value={name} onChange={(e) => setName(e.target.value)} />
                    </Field>
                    <Field>
                        <Label htmlFor="c-subject">Predmet</Label>
                        <Input id="c-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
                    </Field>
                    <Row>
                        <Field>
                            <Label htmlFor="c-audience">Komu</Label>
                            <Select
                                id="c-audience"
                                value={audienceKind}
                                disabled={armed}
                                onChange={(e) => setAudienceKind(e.target.value as AudienceKind)}
                            >
                                <option value="NON_PREMIUM">Len nečlenom (ponuka prémia)</option>
                                <option value="ALL">Všetkým vrátane členov (oznámenie)</option>
                            </Select>
                        </Field>
                        <Field>
                            <Label htmlFor="c-template">Šablóna</Label>
                            <Select
                                id="c-template"
                                value={template}
                                disabled={armed}
                                onChange={(e) => setTemplate(e.target.value as TemplateKind)}
                            >
                                <option value="PODCAST">Podcast (tmavá, ON AIR)</option>
                                <option value="NOTICE">Oznámenie (svetlá, bez prehrávača)</option>
                            </Select>
                        </Field>
                    </Row>
                    {armed && (
                        <Hint>
                            Zoznam príjemcov aj vzhľad sú od pripravenia kampane zmrazené — obe
                            voľby sa preto už nedajú meniť. Ak ich treba zmeniť, vytvorte kópiu.
                        </Hint>
                    )}
                    {audienceKind === 'ALL' && (
                        <WarnBox>
                            <div>
                                Tento e-mail dostanú <strong>aj platiaci členovia a správcovia</strong>.
                                Nikdy takto neposielajte ponuku prémia — je to určené pre oznámenia,
                                zmenu podmienok a zákonné informovanie.
                            </div>
                        </WarnBox>
                    )}
                    {template === 'PODCAST' && (
                    <>
                    <Field>
                        <Label htmlFor="c-header-left">Nápis v hlavičke</Label>
                        <Input
                            id="c-header-left"
                            placeholder="ON AIR (prázdne = predvolené)"
                            maxLength={60}
                            value={headerLeft}
                            onChange={(e) => setHeaderLeft(e.target.value)}
                        />
                    </Field>
                    <Field>
                        <Label htmlFor="c-header-right">Odznak epizódy v hlavičke</Label>
                        <Input
                            id="c-header-right"
                            placeholder="EPIZÓDA 214 (prázdne = automaticky z tokenu)"
                            maxLength={60}
                            value={headerRight}
                            onChange={(e) => setHeaderRight(e.target.value)}
                        />
                    </Field>
                    </>
                    )}
                    <Field>
                        <Label htmlFor="c-body">Text e-mailu</Label>
                        <Textarea id="c-body" value={body} onChange={(e) => setBody(e.target.value)} rows={18} />
                        <Hint>
                            <code>**tučné**</code> · <code>- odrážka</code> · <code>## nadpis</code> ·{' '}
                            <code>[text](odkaz)</code> · <code>{'{{EPIZODA:214|Názov|48 min}}'}</code> vloží
                            prehrávač s pruhom
                        </Hint>
                    </Field>
                    {warnings.length > 0 && (
                        <WarnBox>
                            {warnings.map((w) => (
                                <div key={w}>{w}</div>
                            ))}
                        </WarnBox>
                    )}
                    <Row>
                        <Secondary onClick={onSave} disabled={!!busy}>
                            {busy === 'save' ? 'Ukladám…' : campaign ? 'Uložiť zmeny' : 'Vytvoriť kampaň'}
                        </Secondary>
                        <Secondary onClick={onNewCampaign} disabled={!!busy}>
                            Nová kampaň
                        </Secondary>
                        <Secondary onClick={onDuplicate} disabled={!campaign || !!busy}>
                            Duplikovať
                        </Secondary>
                    </Row>
                    <Hint>
                        {campaign
                            ? 'Odoslanú kampaň už meniť nemožno — „Duplikovať“ z nej spraví novú s rovnakým textom.'
                            : 'Rozpísaná nová kampaň — uloží sa ako nová.'}
                    </Hint>
                </Card>

                <Card>
                    <CardTitle>Náhľad — vykresľuje server</CardTitle>
                    <PreviewFrame title="Náhľad e-mailu" sandbox="" srcDoc={previewHtml} />
                </Card>
            </Cols>

            <Kpis>
                <Kpi>
                    <KpiLabel>Príjemcov</KpiLabel>
                    <KpiValue>{campaign?.totalRecipients ?? audience?.recipients ?? '—'}</KpiValue>
                </Kpi>
                <Kpi>
                    <KpiLabel>Vynechaní členovia</KpiLabel>
                    <KpiValue $ok>{audience?.excludedMembers ?? '—'}</KpiValue>
                </Kpi>
                <Kpi>
                    <KpiLabel>Zdieľané adresy</KpiLabel>
                    <KpiValue>{audience?.sharedAddresses ?? '—'}</KpiValue>
                </Kpi>
                <Kpi>
                    <KpiLabel>Odhlásení</KpiLabel>
                    <KpiValue>{audience?.optedOut ?? '—'}</KpiValue>
                </Kpi>
            </Kpis>

            <Card>
                <CardTitle>Odoslanie</CardTitle>
                <Row>
                    <Secondary onClick={onArm} disabled={!campaign || !!busy || armed}>
                        {busy === 'arm' ? 'Pripravujem…' : 'Pripraviť príjemcov'}
                    </Secondary>
                    <Input
                        style={{ maxWidth: 340 }}
                        placeholder="adresy pre test, oddelené čiarkou (prázdne = tvoja adresa)"
                        value={testTo}
                        onChange={(e) => setTestTo(e.target.value)}
                    />
                    <Secondary onClick={onTest} disabled={!campaign || !!busy}>
                        {busy === 'test' ? 'Posielam…' : 'Odoslať test'}
                    </Secondary>
                </Row>

                <Confirm>
                    <Label htmlFor="c-confirm">Na potvrdenie napíšte počet príjemcov</Label>
                    <Row>
                        <Input
                            id="c-confirm"
                            style={{ width: 130, textAlign: 'center', fontWeight: 700 }}
                            placeholder={String(expected || '')}
                            value={confirmCount}
                            onChange={(e) => setConfirmCount(e.target.value)}
                        />
                        <Primary onClick={() => onSend(nextWave)} disabled={!canSend}>
                            {busy === 'send' ? 'Posielam…' : `Odoslať prvých ${nextWave}`}
                        </Primary>
                        {(busy === 'send' || campaign?.status === 'SENDING') && (
                            <Danger onClick={onStop} disabled={busy === 'stop'}>
                                Zastaviť
                            </Danger>
                        )}
                        {campaign?.killSwitch && busy !== 'send' && (
                            <Secondary onClick={onResume} disabled={!!busy}>
                                Pokračovať
                            </Secondary>
                        )}
                    </Row>
                    <Hint>
                        {!armed && 'Najprv pripravte zoznam príjemcov. '}
                        {armed && !campaign?.testSentAt && 'Test ešte nebol odoslaný — bez neho sa poslať nedá. '}
                        {campaign?.contentChangedSinceTest && 'Text sa po teste zmenil, otestujte znova. '}
                        {campaign?.killSwitch && 'Kampaň je zastavená — najprv „Pokračovať“. '}
                        Prvá vlna je zámerne malá kvôli rozbehu domény.
                    </Hint>
                </Confirm>
            </Card>

            {campaign && armed && (
                <Card>
                    <CardTitle>Priebeh</CardTitle>
                    <Progress>
                        <ProgressBar
                            style={{
                                width: `${expected ? Math.round((campaign.sent / expected) * 100) : 0}%`,
                            }}
                        />
                    </Progress>
                    <Stats>
                        <span>
                            <b>{campaign.sent}</b> odoslaných
                        </span>
                        <span>
                            <b>{campaign.skipped}</b> preskočených
                        </span>
                        <span>
                            <b>{campaign.failed}</b> zlyhalo
                        </span>
                        <span>{campaign.pending} zostáva</span>
                    </Stats>
                    {campaign.uncertain > 0 && (
                        <Uncertain>
                            <b>{campaign.uncertain} v neistom stave.</b> E-mail odišiel, ale zápis do denníka
                            neprešiel — server zámerne nehádal, čo sa stalo. Kým to nerozhodnete, kampaň
                            zostane v stave „Posiela sa“ a týchto {campaign.uncertain} sa počíta do vlny,
                            takže ďalšie odosielanie môže skončiť skôr, než čakáte.
                            <Row style={{ marginTop: 10 }}>
                                <Secondary onClick={() => onResolveUncertain('SENT')} disabled={!!busy}>
                                    Označiť ako odoslané
                                </Secondary>
                                <Danger onClick={() => onResolveUncertain('FAILED')} disabled={!!busy}>
                                    Označiť ako zlyhané — môžu dostať e-mail druhýkrát
                                </Danger>
                            </Row>
                        </Uncertain>
                    )}
                    {campaign.failed > 0 && (
                        <Row style={{ marginTop: 12 }}>
                            <Secondary onClick={onRequeueFailed} disabled={!!busy}>
                                Skúsiť neúspešných znova ({campaign.failed})
                            </Secondary>
                        </Row>
                    )}
                </Card>
            )}

            {recipients.length > 0 && (
                <Card>
                    <CardTitle>Príjemcovia</CardTitle>
                    <TableWrap>
                        <Table>
                            <thead>
                                <tr>
                                    <th>E-mail</th>
                                    <th>Stav</th>
                                    <th>Poznámka</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recipients.map((r, i) => (
                                    <tr key={`${r.email}-${i}`}>
                                        <td>{r.email}</td>
                                        <td>{r.status}</td>
                                        <td>{r.skipReason || r.error || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </TableWrap>
                </Card>
            )}

            {loading && <Hint>Načítavam…</Hint>}
        </Page>
    );
};

const DEFAULT_NAME = 'Epizóda — štart sezóny';
const DEFAULT_SUBJECT = 'Epizóda 214 — v piatok štartuje sezóna';

const DEFAULT_BODY = `## Štartujeme tretiu sezónu

Kamaráti, v piatok sa naplno rozbieha nová futbalová sezóna — a my máme pre vás poriadnu epizódu na úvod.

{{EPIZODA:214|Štart sezóny|48 min}}

[Počúvať zadarmo](https://goly-esa.sk)

## Čo máte ako člen

- Celé epizódy podcastu
- Futbalové analýzy
- Dlhodobé aj krátkodobé tikety
- Prémiový obsah počas celej sezóny

Ideme do tretej spoločnej sezóny. Verím, že si ju užijeme rovnako ako tie predošlé.

**Góly a Esá**`;

/* ---------------- styles ---------------- */

/** Full width, unlike the other admin screens — the preview needs the room. */
const Page = styled.div`
    width: 100%;
    margin: 0;
    padding: 20px 28px 60px;

    @media (max-width: 700px) {
        padding: 16px 14px 40px;
    }
`;

const Top = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 18px;
`;

const Back = styled.button`
    width: 34px;
    height: 34px;
    border: 1px solid #ddd;
    background: #fff;
    border-radius: 8px;
    font-size: 17px;
    cursor: pointer;
`;

const Heading = styled.h1`
    flex: 1;
    font-size: 24px;
    font-weight: 700;
    color: #141210;
    margin: 0;
`;

const Chip = styled.span<{ $tone: string }>`
    font-size: 12px;
    font-weight: 600;
    padding: 5px 12px;
    border-radius: 20px;
    background: ${(p) => (p.$tone === 'SENDING' ? '#e8f0fe' : p.$tone === 'SENT' ? '#eef7ee' : '#fff4e5')};
    color: ${(p) => (p.$tone === 'SENDING' ? '#1a5fb4' : p.$tone === 'SENT' ? '#256029' : '#8a5300')};
`;

const Cols = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 16px;
    align-items: start;

    @media (max-width: 1000px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const Card = styled.div`
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08);
    padding: 18px 20px;
    margin-bottom: 16px;
`;

const CardTitle = styled.h2`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.13em;
    text-transform: uppercase;
    color: #75716b;
    margin: 0 0 14px;
`;

const Field = styled.div`
    margin-bottom: 14px;
`;

const Label = styled.label`
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #141210;
    margin-bottom: 6px;
`;

const Input = styled.input`
    width: 100%;
    font: inherit;
    font-size: 14px;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 5px;

    &:focus {
        outline: none;
        border-color: #f56607;
        box-shadow: 0 0 0 3px rgba(245, 102, 7, 0.13);
    }
`;

const Textarea = styled.textarea`
    width: 100%;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px;
    line-height: 1.65;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 5px;
    resize: vertical;

    &:focus {
        outline: none;
        border-color: #f56607;
        box-shadow: 0 0 0 3px rgba(245, 102, 7, 0.13);
    }
`;

const PreviewFrame = styled.iframe`
    width: 100%;
    height: 720px;
    border: 1px solid #eee;
    border-radius: 6px;
    background: #dfe2e6;
`;

const Select = styled.select`
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d7dade;
    border-radius: 8px;
    background: #fff;
    font-size: 14px;
    color: #16181d;

    &:disabled {
        background: #f2f3f5;
        color: #7a7f88;
        cursor: not-allowed;
    }
`;

const Hint = styled.p`
    font-size: 12px;
    line-height: 1.6;
    color: #777;
    margin: 8px 0 0;

    code {
        background: #f4f5f6;
        padding: 1px 5px;
        border-radius: 3px;
    }
`;

const WarnBox = styled.div`
    background: #fff4e5;
    color: #8a5300;
    border-radius: 6px;
    padding: 10px 13px;
    font-size: 13px;
    line-height: 1.6;
    margin-bottom: 14px;
`;

const Row = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    align-items: center;
`;

const Confirm = styled.div`
    border-top: 1px solid #eee;
    margin-top: 16px;
    padding-top: 16px;
`;

const Secondary = styled.button`
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    padding: 11px 20px;
    border-radius: 5px;
    border: 1px solid #ddd;
    background: #fff;
    color: #141210;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;

const Primary = styled(Secondary)`
    background: #f56607;
    border-color: #f56607;
    color: #fff;
`;

const Danger = styled(Secondary)`
    border-color: #e3b3b3;
    color: #a11;
`;

const Banner = styled.p<{ $ok?: boolean; $warn?: boolean }>`
    background: ${(p) => (p.$warn ? '#fff4e5' : p.$ok ? '#eef7ee' : '#fdecec')};
    color: ${(p) => (p.$warn ? '#8a5300' : p.$ok ? '#256029' : '#a11')};
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 13.5px;
    line-height: 1.6;
    margin: 0 0 16px;
`;

const Kpis = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 12px;
    margin-bottom: 16px;
`;

const Kpi = styled.div`
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08);
    padding: 15px 17px;
`;

const KpiLabel = styled.div`
    font-size: 12px;
    color: #777;
    margin-bottom: 5px;
`;

const KpiValue = styled.div<{ $ok?: boolean }>`
    font-size: 27px;
    font-weight: 700;
    color: ${(p) => (p.$ok ? '#256029' : '#141210')};
`;

const Progress = styled.div`
    height: 9px;
    background: #edeeef;
    border-radius: 5px;
    overflow: hidden;
    margin: 4px 0 10px;
`;

const ProgressBar = styled.div`
    height: 100%;
    background: #f56607;
`;

const Stats = styled.div`
    display: flex;
    gap: 18px;
    flex-wrap: wrap;
    font-size: 13px;
    color: #777;

    b {
        color: #141210;
    }
`;

/** Amber, not green and not red: nothing has gone wrong, but a human has to decide something. */
const Uncertain = styled.div`
    background: #fff4e5;
    color: #8a5300;
    border-radius: 6px;
    padding: 12px 14px;
    font-size: 13px;
    line-height: 1.6;
    margin-top: 14px;
`;

const TableWrap = styled.div`
    overflow-x: auto;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        text-align: left;
        font-size: 11px;
        letter-spacing: 0.09em;
        text-transform: uppercase;
        color: #777;
        padding: 0 0 9px;
        border-bottom: 1px solid #eee;
    }

    td {
        padding: 10px 0;
        border-bottom: 1px solid #f2f2f0;
    }
`;

export default Campaigns;
